import { useEffect, useState } from 'react';
import peaceBg from '@/assets/peace-clarity-bg.png';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, BookOpen, Feather, Heart, Plus, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GuidedTour, TourTriggerButton } from '@/components/dashboard/GuidedTour';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useAuth } from '@/hooks/useAuth';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { AnnouncementCard } from '@/components/dashboard/AnnouncementCard';
import { ProgressVisualization } from '@/components/dashboard/ProgressVisualization';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { LivingTreeV5 } from '@/components/living-tree/LivingTreeV5';
import { DeleteAnnouncementDialog } from '@/components/dashboard/DeleteAnnouncementDialog';
import { CreditDisplay } from '@/components/dashboard/CreditDisplay';
import { EngagementCard } from '@/components/dashboard/EngagementCard';
import { ImpactNudge } from '@/components/dashboard/ImpactNudge';
import { SoftEncouragement } from '@/components/dashboard/SoftEncouragement';
import { PostPublishSuccessCard } from '@/components/dashboard/PostPublishSuccessCard';
import { MagazineFlipbook } from '@/components/dashboard/MagazineFlipbook';
import { TreslogUpsellCard } from '@/components/dashboard/TreslogUpsellCard';
import { TreslogInlineHint } from '@/components/dashboard/TreslogInlineHint';
import { UpsellModal, useUpsellModal } from '@/components/credits/UpsellModals';
import { ConnectionGrowthMeter } from '@/components/dashboard/ConnectionGrowthMeter';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useInfluencerAccess } from '@/hooks/useInfluencerAccess';
import { useQuietMode } from '@/hooks/useQuietMode';
import { usePostPublishSuccess } from '@/hooks/usePostPublishSuccess';
import { useFounderStatus } from '@/hooks/useFounderStatus';
import { FounderReminderBanner } from '@/components/founder/FounderReminderBanner';
import { FounderExpiryNotice } from '@/components/founder/FounderExpiryNotice';
import { FounderWelcomeCard } from '@/components/founder/FounderWelcomeCard';
import { FounderSuggestionsCard } from '@/components/founder/FounderSuggestionsCard';
import { useEventTracking } from '@/hooks/useEventTracking';
import { DashboardReturnBanner } from '@/components/dashboard/DashboardReturnBanner';
import { IdentityMilestone } from '@/components/dashboard/IdentityMilestone';
import { RecentRepliesCard } from '@/components/dashboard/RecentRepliesCard';
import { QuietDraftShelf, QuietDraftNudge } from '@/components/dashboard/QuietDraftShelf';
import { JacKeyDraftNudge } from '@/components/dashboard/JacKeyDraftNudge';
import { MomentsThatMatterPanel } from '@/components/dashboard/MomentsThatMatterPanel';
import { TreeAwakening } from '@/components/dashboard/TreeAwakening';
import { ReferralProgressPanel } from '@/components/dashboard/ReferralProgressPanel';
import { ShareTreeProgress } from '@/components/dashboard/ShareTreeProgress';
import { ConversationsPanel } from '@/components/dashboard/ConversationsPanel';
import { ConnectionBloomDisplay } from '@/components/dashboard/ConnectionBloomDisplay';
import { ImpactLeaderboard } from '@/components/dashboard/ImpactLeaderboard';
import { TopVoiceWinnerBanner } from '@/components/dashboard/TopVoiceWinnerBanner';

import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Announcement } from '@/types/announcement';

// Note: recipient_email is intentionally excluded from direct table SELECT
// (column-level revoked). The RPC fallback get_my_announcements_for_dashboard
// returns it for owner-side rendering.
const DASHBOARD_ANNOUNCEMENT_SELECT = 'id, user_id, recipient_name, relationship, message, template, photo_urls, status, public_slug, created_at, updated_at, published_at, sender_display_name, is_anonymous, courage_mode, delivery_mode, metadata, animation_theme';

const unwrapDashboardRows = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, any>;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.rows)) return obj.rows;
    if (obj.data && typeof obj.data === 'object') return [obj.data];
  }
  return [];
};

async function fetchDashboardAnnouncements(userId: string) {
  const direct = await supabase
    .from('announcements')
    .select(DASHBOARD_ANNOUNCEMENT_SELECT)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  const directRows = unwrapDashboardRows(direct.data);
  if (!direct.error && directRows.length > 0) return { data: directRows, error: null };

  const fallback = await (supabase as any).rpc('get_my_announcements_for_dashboard');
  const fallbackRows = unwrapDashboardRows(fallback.data);
  console.log('[Dashboard] announcement loader fallback response', {
    directError: direct.error?.message || null,
    fallbackError: fallback.error?.message || null,
    rowCount: fallbackRows.length,
    ids: fallbackRows.slice(0, 10).map((row: any) => ({
      dashboardDraftId: row?.draft_id ?? row?.draftId ?? row?.id ?? null,
      announcementId: row?.announcement_id ?? row?.announcementId ?? row?.id ?? null,
      metadataDraftId: row?.metadata?.draftId ?? row?.metadata?.draft_id ?? null,
    })),
  });
  return {
    data: fallbackRows.length > 0 ? fallbackRows : directRows,
    error: fallbackRows.length > 0 || !direct.error ? null : (fallback.error ?? direct.error),
  };
}

const welcomeCopy = {
  en: { greeting: 'Welcome back', subtext: 'Who comes to mind today?', create: 'Write Something', announcements: 'Your Moments', tellFriend: 'Tell a Friend' },
  es: { greeting: 'Bienvenido de vuelta', subtext: '¿Quién viene a tu mente hoy?', create: 'Escribe Algo', announcements: 'Tus Momentos', tellFriend: 'Invita a un Amigo' },
  fr: { greeting: 'Bienvenue', subtext: 'Qui vous vient à l\'esprit ?', create: 'Écrire Quelque Chose', announcements: 'Vos Moments', tellFriend: 'Parlez-en à un Ami' },
  pt: { greeting: 'Bem-vindo de volta', subtext: 'Quem vem à mente hoje?', create: 'Escreva Algo', announcements: 'Seus Momentos', tellFriend: 'Indique um Amigo' },
  ar: { greeting: 'مرحباً بعودتك', subtext: 'من يخطر ببالك اليوم؟', create: 'اكتب شيئاً', announcements: 'لحظاتك', tellFriend: 'أخبر صديقًا' },
  ja: { greeting: 'おかえりなさい', subtext: '今日、誰が思い浮かびますか？', create: '何か書く', announcements: 'あなたのモーメント', tellFriend: '友達に教える' },
  ko: { greeting: '다시 오셨군요', subtext: '오늘 누가 떠오르나요?', create: '무언가 쓰기', announcements: '당신의 순간들', tellFriend: '친구에게 알리기' },
  id: { greeting: 'Selamat datang kembali', subtext: 'Siapa yang terlintas hari ini?', create: 'Tulis Sesuatu', announcements: 'Momen Anda', tellFriend: 'Beritahu Teman' },
  hi: { greeting: 'वापस स्वागत है', subtext: 'आज कौन याद आ रहा है?', create: 'कुछ लिखें', announcements: 'आपके पल', tellFriend: 'किसी दोस्त को बताएं' },
};

const affirmationBannerCopy = {
  en: { title: 'Turn Your Photo Into Art', subtitle: 'See yourself through words of affirmation — it only takes a moment', cta: 'Try It Now' },
  es: { title: 'Convierte Tu Foto en Arte', subtitle: 'Mírate a través de palabras de afirmación — solo toma un momento', cta: 'Pruébalo Ahora' },
  fr: { title: 'Transformez Votre Photo en Art', subtitle: 'Voyez-vous à travers des mots d\'affirmation — ça ne prend qu\'un instant', cta: 'Essayer' },
  pt: { title: 'Transforme Sua Foto em Arte', subtitle: 'Veja-se através de palavras de afirmação — leva apenas um momento', cta: 'Experimente Agora' },
  ar: { title: 'حوّل صورتك إلى فن', subtitle: 'انظر لنفسك من خلال كلمات التأكيد — لا يستغرق سوى لحظة', cta: 'جربه الآن' },
  ja: { title: '写真をアートに', subtitle: 'アファメーションの言葉で自分を見てみましょう — ほんの一瞬です', cta: '試してみる' },
  ko: { title: '사진을 예술로 바꾸세요', subtitle: '긍정의 말로 자신을 바라보세요 — 잠깐이면 됩니다', cta: '지금 해보기' },
  id: { title: 'Ubah Foto Anda Menjadi Seni', subtitle: 'Lihat diri Anda melalui kata-kata afirmasi — hanya butuh sebentar', cta: 'Coba Sekarang' },
  hi: { title: 'अपनी फ़ोटो को कला में बदलें', subtitle: 'प्रेरणादायक शब्दों के ज़रिए खुद को देखें — बस एक पल लगता है', cta: 'अभी आज़माएं' },
};

export default function Dashboard() {
  const { t, language } = useLanguage();
  const { user, isHydrated } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useEventTracking();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<Announcement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMagazine, setShowMagazine] = useState(false);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const { credits } = useUserCredits();
  const { quietMode } = useQuietMode();
  const { modalState, showNoCreditsModal, setModalOpen } = useUpsellModal();
  const { showSuccessCard, publicSlug: successSlug, recipientName: successRecipientName, dismissSuccess } = usePostPublishSuccess();
  const [showTreeAwakening, setShowTreeAwakening] = useState(false);
  const { founder, isFounder, isActiveFounder, isExpiredFounder, graceDaysRemaining, shouldShowReminder, dismissReminder } = useFounderStatus();
  const [showExpiryNotice, setShowExpiryNotice] = useState(false);

  useEffect(() => {
    if (showSuccessCard) {
      setShowTreeAwakening(true);
    }
  }, [showSuccessCard]);

  const wc = lc(welcomeCopy, language);
  const affirmationCopy = lc(affirmationBannerCopy, language);
  const userName = user?.user_metadata?.full_name?.split(' ')[0] || '';

  // Dashboard load diagnostics — helps debug mobile empty-dashboard issues
  useEffect(() => {
    if (!isHydrated) return;
    const userPlan = credits?.highest_bundle_purchased || 'starter';
    trackEvent('dashboard_loaded', {
      locale: language,
      user_plan: userPlan,
      host: window.location.hostname,
      auth_valid: !!user,
      credits_loaded: !!credits,
      founder_loaded: isFounder,
      announcements_count: announcements.length,
      is_mobile: window.innerWidth < 768,
      user_agent: navigator.userAgent.slice(0, 120),
    });
  }, [isHydrated, language, credits?.highest_bundle_purchased, trackEvent, user, credits, isFounder, announcements.length]);

  useEffect(() => {
    if (isExpiredFounder) {
      setShowExpiryNotice(true);
    }
  }, [isExpiredFounder]);

  const hasPurchasedBundle = credits?.has_purchased_bundle ?? false;
  const { hasInfluencerAccess: _infAccess } = useInfluencerAccess();
  const hasLegacyPlan = credits?.highest_bundle_purchased === 'legacy' || _infAccess;
  const publishedAnnouncements = announcements.filter(a => a.status === 'approved');
  const draftAnnouncements = announcements.filter(a => a.status === 'draft');

  useEffect(() => {
    if (publishedAnnouncements.length > 0 && !quietMode) {
      const timer = setTimeout(() => {
        const lastDismissed = localStorage.getItem('encouragement_dismissed');
        const hourAgo = Date.now() - 3600000;
        if (!lastDismissed || parseInt(lastDismissed) < hourAgo) {
          setShowEncouragement(true);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [publishedAnnouncements.length, quietMode]);

  const handleDismissEncouragement = () => {
    setShowEncouragement(false);
    localStorage.setItem('encouragement_dismissed', Date.now().toString());
  };

  const handleGetMoreCredits = () => {
    navigate('/add-credits');
  };

  // Sanitize announcement rows — skip malformed records instead of crashing
  const sanitizeAnnouncements = (rows: any[]): Announcement[] => {
    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => {
      try {
        if (!row || typeof row !== 'object') {
          console.warn('[Dashboard] Skipping malformed announcement:', row?.id);
          return false;
        }
        const rowAny = row as any;
        const dashboardDraftId = rowAny.id;
        const announcementId = rowAny.announcement_id || rowAny.announcementId || rowAny.id;
        if (announcementId && announcementId !== rowAny.id) {
          rowAny.metadata = { ...(typeof rowAny.metadata === 'object' && rowAny.metadata ? rowAny.metadata : {}), dashboardDraftId };
          rowAny.id = announcementId;
        } else if (!rowAny.id && announcementId) {
          rowAny.id = announcementId;
        }
        if (!rowAny.user_id && rowAny.userId) rowAny.user_id = rowAny.userId;
        if (!rowAny.id || !rowAny.user_id) {
          console.warn('[Dashboard] Skipping malformed announcement:', { id: rowAny.id, announcement_id: rowAny.announcement_id, user_id: rowAny.user_id });
          return false;
        }
        console.log('[Dashboard] normalized draft id mapping', {
          dashboardDraftId: rowAny.metadata?.dashboardDraftId ?? rowAny.metadata?.draftId ?? rowAny.metadata?.draft_id ?? rowAny.id,
          announcementId: rowAny.id,
          status: rowAny.status,
        });
        // Ensure photo_urls is always an array
        if (rowAny.photo_urls && !Array.isArray(rowAny.photo_urls)) {
          rowAny.photo_urls = [];
        }
        // Ensure metadata is safe
        if (rowAny.metadata && typeof rowAny.metadata !== 'object') {
          rowAny.metadata = {};
        }
        return true;
      } catch {
        console.warn('[Dashboard] Error sanitizing announcement, skipping:', row?.id);
        return false;
      }
    }) as Announcement[];
  };

  const retryFetchAnnouncements = async () => {
    if (!user?.id) return;
    setFetchError(false);
    setIsLoading(true);
    
    console.log('[Dashboard] Manual retry fetch for user:', user.id);
    const { data, error } = await fetchDashboardAnnouncements(user.id);

    if (error) {
      console.error('[Dashboard] Retry fetch failed:', error);
      setFetchError(true);
    } else {
      const rows = sanitizeAnnouncements(data ?? []);
      setAnnouncements(rows);
      setFetchError(false);
      try {
        localStorage.setItem(`sin_dashboard_announcements_${user.id}`, JSON.stringify(rows));
      } catch { /* mobile storage safety */ }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isHydrated) {
      console.log('[Dashboard] Waiting for auth hydration...');
      return;
    }

    if (!user) {
      console.log('[Dashboard] No user after hydration, clearing announcements');
      setAnnouncements([]);
      setIsLoading(false);
      setFetchError(false);
      return;
    }

    let cancelled = false;
    const cacheKey = `sin_dashboard_announcements_${user.id}`;

    console.log('[Dashboard] Auth ready, loading announcements for:', user.id);

    // Show cached data instantly to avoid empty-state flash
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = sanitizeAnnouncements(JSON.parse(cached));
        if (parsed.length > 0) {
          setAnnouncements(parsed);
          setIsLoading(false);
        }
      }
    } catch {
      // localStorage may throw in private/incognito mobile browsers
    }

    const loadAnnouncements = async (attempt = 1) => {
      console.log(`[Dashboard] Fetch attempt ${attempt}`);
      const { data, error } = await fetchDashboardAnnouncements(user.id);

      if (cancelled) return;

      if (error) {
        console.error(`[Dashboard] Fetch attempt ${attempt} failed:`, error.message);
        // Retry once automatically on first failure
        if (attempt === 1) {
          console.log('[Dashboard] Retrying in 2s...');
          setTimeout(() => { if (!cancelled) loadAnnouncements(2); }, 2000);
          return;
        }
        // After retry, only show error if no cached data
        if (announcements.length === 0) {
          setFetchError(true);
        }
      } else {
        console.log(`[Dashboard] Fetch success: ${(data ?? []).length} announcements`);
        const rows = sanitizeAnnouncements(data ?? []);
        setAnnouncements(rows);
        setFetchError(false);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(rows));
        } catch {
          // localStorage may throw in private/incognito mobile browsers
        }
      }

      setIsLoading(false);
    };

    loadAnnouncements();

    return () => { cancelled = true; };
  }, [isHydrated, user?.id]);

  const handleDeleteClick = (announcement: Announcement) => {
    setAnnouncementToDelete(announcement);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!announcementToDelete || !user) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', announcementToDelete.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setAnnouncements((prev) => prev.filter((a) => a.id !== announcementToDelete.id));
      toast.success(({ en: 'Announcement deleted', es: 'Anuncio eliminado', fr: 'Annonce supprimée', pt: 'Anúncio excluído', ar: 'تم حذف الإعلان', ja: 'アナウンス削除済み', ko: '공지 삭제됨', id: 'Pengumuman dihapus' } as Record<string, string>)[language] || 'Announcement deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(({ en: 'Failed to delete announcement', es: 'Error al eliminar el anuncio', fr: 'Échec de la suppression', pt: 'Falha ao excluir anúncio', ar: 'فشل حذف الإعلان', ja: '削除に失敗しました', ko: '삭제 실패', id: 'Gagal menghapus pengumuman' } as Record<string, string>)[language] || 'Failed to delete announcement');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAnnouncementToDelete(null);
    }
  };

  return (
    <div className="min-h-screen relative z-0">
      <div
        className="fixed inset-0 pointer-events-none opacity-30 dark:opacity-25 -z-10"
        style={{
          backgroundImage: `url(${peaceBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <AuthenticatedHeader />

      <DeleteAnnouncementDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        announcement={announcementToDelete}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
      />

      <UpsellModal
        type={modalState.type}
        open={modalState.open}
        onOpenChange={setModalOpen}
        creditsAdded={modalState.creditsAdded}
      />

      {/* Welcome Gradient Banner */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-20 -right-20 h-[300px] w-[300px] rounded-full bg-primary/8 blur-[80px]"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-10 -left-10 h-[200px] w-[200px] rounded-full bg-accent/8 blur-[60px]"
            animate={{ scale: [1.1, 1, 1.1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="container relative z-10 py-10 md:py-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <motion.p
                className="text-sm font-medium text-primary mb-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {wc.greeting}{userName ? `, ${userName}` : ''} 💚
              </motion.p>
              <h1 className="text-3xl md:text-4xl font-heading font-bold billboard-flash !leading-tight">
                {wc.subtext}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-3 w-full">
              <TourTriggerButton onClick={() => setShowTour(true)} />
              <Link to="/create" data-tour="create-btn" className="flex-1 min-w-0">
                <Button size="lg" className="w-full gap-2 rounded-2xl shadow-lg shadow-primary/20 group px-5">
                  <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                  {wc.create}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <main className="container py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Founder Welcome Card */}
          {isActiveFounder && (
            <div className="mb-6">
              <FounderWelcomeCard userName={userName} slotNumber={founder?.slot_number} />
            </div>
          )}

          {/* Founder Reminder Banner */}
          <FounderReminderBanner
            daysRemaining={graceDaysRemaining ?? 0}
            onDismiss={dismissReminder}
            show={shouldShowReminder}
          />

          {/* Founder Expiry Notice */}
          <FounderExpiryNotice
            show={showExpiryNotice}
            onDismiss={() => setShowExpiryNotice(false)}
          />

          {/* Connection Bloom Display */}
          <div className="mb-6">
            <ConnectionBloomDisplay />
          </div>

          <div className="mb-8 space-y-6">
            <ConversationsPanel />
            <RecentRepliesCard announcements={announcements} />
          </div>

          {/* Living Tree — First thing users see */}
          <div data-tour="announcements" className="mb-8">
            {isLoading ? (
              <Card className="glass border-0 rounded-2xl">
                <CardContent className="flex items-center justify-center py-16">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    >
                      <Sparkles className="h-5 w-5 text-primary" />
                    </motion.div>
                    <span className="text-muted-foreground">
                      {({ en: 'Loading...', es: 'Cargando...', fr: 'Chargement...', pt: 'Carregando...', ar: 'جارٍ التحميل...', ja: '読み込み中...', ko: '로딩 중...', id: 'Memuat...' } as Record<string, string>)[language] || 'Loading...'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ) : fetchError && announcements.length === 0 ? (
              <Card className="glass border-0 rounded-2xl">
                <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
                  <p className="text-muted-foreground text-center">
                    {({ en: "We couldn't load your announcements right now.", es: 'No pudimos cargar tus anuncios.', fr: 'Impossible de charger vos annonces.', pt: 'Não foi possível carregar seus anúncios.', ar: 'لم نتمكن من تحميل إعلاناتك.', ja: 'アナウンスを読み込めませんでした。', ko: '공지를 불러올 수 없습니다.', id: 'Tidak dapat memuat pengumuman Anda.' } as Record<string, string>)[language] || "We couldn't load your announcements right now."}
                  </p>
                  <Button onClick={retryFetchAnnouncements} variant="outline" className="gap-2 rounded-2xl">
                    {({ en: 'Try Again', es: 'Reintentar', fr: 'Réessayer', pt: 'Tentar Novamente', ar: 'حاول مجدداً', ja: '再試行', ko: '다시 시도', id: 'Coba Lagi' } as Record<string, string>)[language] || 'Try Again'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <LivingTreeV5 announcements={announcements} />
                {!hasLegacyPlan && !quietMode && publishedAnnouncements.length > 0 && (
                  <TreslogInlineHint className="mt-8" />
                )}
                {announcements.length === 0 && !hasLegacyPlan && !quietMode && <TreslogUpsellCard />}

                {/* Magazine View Toggle + TRESLOG + Refer */}
                <div className="flex flex-wrap items-center gap-3">
                  <ShareTreeProgress fruitCount={publishedAnnouncements.length} />
                  {hasPurchasedBundle && publishedAnnouncements.length > 0 && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2 rounded-2xl glass border-border/50"
                      onClick={() => setShowMagazine(true)}
                    >
                      <BookOpen className="h-5 w-5" />
                      {t.howToUse.dashboard.viewAsMagazine}
                    </Button>
                  )}

                  {hasLegacyPlan && (
                    <Link to="/treslog">
                      <Button size="lg" variant="outline" className="gap-2 rounded-2xl glass border-border/50">
                        <Feather className="h-5 w-5" />
                        TRESLOG
                      </Button>
                    </Link>
                  )}

                  <Link to="/refer">
                    <Button size="lg" variant="outline" className="gap-2 rounded-2xl glass border-border/50">
                      <Users className="h-5 w-5" />
                      {wc.tellFriend}
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Impact Leaderboard — directly after Living Tree */}
            <div className="mt-6">
              <ImpactLeaderboard />
            </div>
          </div>

          {/* Top Voice Winner Celebration */}
          <TopVoiceWinnerBanner />

          {/* Dashboard Return Banner */}
          <DashboardReturnBanner />

          {/* Identity Milestone */}
          <IdentityMilestone />

          {/* Quiet Draft Nudge */}
          <QuietDraftNudge draftCount={draftAnnouncements.length} firstDraftId={draftAnnouncements[0]?.id} />

          {/* Post-Publish Success Card */}
          {showSuccessCard && (
            <div className="mb-8">
              <PostPublishSuccessCard 
                onDismiss={dismissSuccess}
                publicSlug={successSlug}
                recipientName={successRecipientName}
              />
            </div>
          )}


          {/* Referral Progress Panel */}
          <div className="mb-8">
            <ReferralProgressPanel />
          </div>



          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="mb-8 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-emerald-600 to-emerald-500 p-6 md:p-8 cursor-pointer group"
            onClick={() => navigate('/affirmation-images')}
          >
            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_3s_ease-in-out_infinite] pointer-events-none" />

            <div className="relative z-10 flex flex-col sm:flex-row items-center gap-5">
              {/* Glowing icon */}
              <div className="shrink-0 p-4 rounded-2xl bg-white/15 ring-2 ring-white/25 shadow-lg shadow-emerald-900/20 group-hover:scale-105 transition-transform duration-300">
                <Sparkles className="h-8 w-8 text-white drop-shadow-md" />
              </div>

              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight leading-tight">
                  ✨ {affirmationCopy.title}
                </h2>
                <p className="text-white/80 text-sm md:text-base mt-1.5 leading-relaxed">
                  {affirmationCopy.subtitle}
                </p>
              </div>

              <Button
                variant="secondary"
                size="lg"
                className="shrink-0 bg-white text-primary font-bold shadow-elevation-2 hover:bg-white/90 hover:shadow-elevation-3 transition-all"
                onClick={(e) => { e.stopPropagation(); navigate('/affirmation-images'); }}
              >
                {affirmationCopy.cta}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </motion.div>

          {/* Credits and Engagement */}
          <div className="grid gap-6 md:grid-cols-2 mb-8">
            <div data-tour="credits">
              <CreditDisplay onGetMore={handleGetMoreCredits} recentAnnouncements={announcements} />
            </div>
            <div data-tour="engagement">
              <EngagementCard />
            </div>
          </div>

          {publishedAnnouncements.length > 0 && (
            <div className="mb-8">
              <ConnectionGrowthMeter announcements={announcements} quietMode={quietMode} />
            </div>
          )}

          {/* Impact Nudge — soft streak */}
          {!quietMode && publishedAnnouncements.length > 0 && (
            <div className="mb-6">
              <ImpactNudge publishedCount={publishedAnnouncements.length} />
            </div>
          )}

          {/* Soft Encouragement */}
          {showEncouragement && !quietMode && (
            <div className="mb-8">
              <SoftEncouragement 
                show={showEncouragement} 
                onDismiss={handleDismissEncouragement} 
              />
            </div>
          )}

          {/* Additional dashboard content follows */}

          {/* Additional dashboard content follows */}

          {/* Moments That Matter — between Living Tree and Quiet Drafts */}
          {!isLoading && !quietMode && announcements.length > 0 && (
            <MomentsThatMatterPanel announcements={announcements} />
          )}

          {/* JacKey Draft Nudge — above Quiet Drafts */}
          {!isLoading && !quietMode && (
            <JacKeyDraftNudge drafts={draftAnnouncements} />
          )}

          {/* Quiet Drafts Shelf — below Living Tree */}
          {!isLoading && (
            <QuietDraftShelf drafts={draftAnnouncements} onDelete={handleDeleteClick} />
          )}
        </motion.div>
      </main>

      {/* Magazine Flipbook Modal */}
      <AnimatePresence>
        {showMagazine && publishedAnnouncements.length > 0 && (
          <MagazineFlipbook
            announcements={publishedAnnouncements}
            onClose={() => setShowMagazine(false)}
          />
        )}
      </AnimatePresence>

      <GuidedTour isActive={showTour} onEnd={() => setShowTour(false)} />

      {/* Tree Awakening — cinematic post-publish animation */}
      <TreeAwakening
        open={showTreeAwakening}
        onComplete={() => {
          setShowTreeAwakening(false);
          dismissSuccess();
        }}
        recipientName={successRecipientName || undefined}
      />
    </div>
  );
}
